import { useState, useEffect, useCallback } from 'react';
import { useSession } from '@clerk/clerk-react';
import { fetchWithAuth } from 'shared/api/fetch-with-auth';
import { API_URL } from 'constants/index';
import {
  Segment,
  CreateSegmentDto,
  UpdateSegmentDto,
  SegmentEvaluationResult,
} from 'shared/types/segment';

// ============================================
// Fetch All Segments
// ============================================

interface UseSegmentsReturn {
  segments: Segment[];
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export const useSegments = (label?: string): UseSegmentsReturn => {
  const { session } = useSession();
  const [segments, setSegments] = useState<Segment[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchSegments = useCallback(async () => {
    if (!session) return;
    try {
      setIsLoading(true);
      setError(null);
      const url = label
        ? `${API_URL}/segments?label=${encodeURIComponent(label)}`
        : `${API_URL}/segments`;
      const response = await fetchWithAuth(url, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      }, session);

      if (!response.ok) {
        throw new Error(`Failed to fetch segments: ${response.statusText}`);
      }

      const data = await response.json();
      setSegments(Array.isArray(data) ? data : []);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Kon segmenten niet ophalen';
      console.error('Error fetching segments:', err);
      setError(message);
      setSegments([]);
    } finally {
      setIsLoading(false);
    }
  }, [session, label]);

  useEffect(() => {
    fetchSegments();
  }, [fetchSegments]);

  return { segments, isLoading, error, refetch: fetchSegments };
};

// ============================================
// Fetch Single Segment
// ============================================

interface UseSegmentReturn {
  segment: Segment | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export const useSegment = (id: string | undefined): UseSegmentReturn => {
  const { session } = useSession();
  const [segment, setSegment] = useState<Segment | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchSegment = useCallback(async () => {
    if (!session || !id || id === 'new') {
      setIsLoading(false);
      return;
    }
    try {
      setIsLoading(true);
      setError(null);
      const response = await fetchWithAuth(`${API_URL}/segments/${id}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      }, session);

      if (!response.ok) {
        throw new Error(`Failed to fetch segment: ${response.statusText}`);
      }

      const data = await response.json();
      setSegment(data);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Kon segment niet ophalen';
      console.error('Error fetching segment:', err);
      setError(message);
    } finally {
      setIsLoading(false);
    }
  }, [session, id]);

  useEffect(() => {
    fetchSegment();
  }, [fetchSegment]);

  return { segment, isLoading, error, refetch: fetchSegment };
};

// ============================================
// Create Segment
// ============================================

interface UseCreateSegmentReturn {
  createSegment: (dto: CreateSegmentDto) => Promise<Segment>;
  isCreating: boolean;
  error: string | null;
}

export const useCreateSegment = (): UseCreateSegmentReturn => {
  const { session } = useSession();
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const createSegment = useCallback(async (dto: CreateSegmentDto): Promise<Segment> => {
    if (!session) throw new Error('Geen actieve sessie');
    try {
      setIsCreating(true);
      setError(null);
      const response = await fetchWithAuth(`${API_URL}/segments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(dto),
      }, session);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to create segment: ${response.statusText}`);
      }

      return await response.json();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Kon segment niet aanmaken';
      console.error('Error creating segment:', err);
      setError(message);
      throw err;
    } finally {
      setIsCreating(false);
    }
  }, [session]);

  return { createSegment, isCreating, error };
};

// ============================================
// Update Segment
// ============================================

interface UseUpdateSegmentReturn {
  updateSegment: (id: number, dto: UpdateSegmentDto) => Promise<Segment>;
  isUpdating: boolean;
  error: string | null;
}

export const useUpdateSegment = (): UseUpdateSegmentReturn => {
  const { session } = useSession();
  const [isUpdating, setIsUpdating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const updateSegment = useCallback(async (id: number, dto: UpdateSegmentDto): Promise<Segment> => {
    if (!session) throw new Error('Geen actieve sessie');
    try {
      setIsUpdating(true);
      setError(null);
      const response = await fetchWithAuth(`${API_URL}/segments/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(dto),
      }, session);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to update segment: ${response.statusText}`);
      }

      return await response.json();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Kon segment niet bijwerken';
      console.error('Error updating segment:', err);
      setError(message);
      throw err;
    } finally {
      setIsUpdating(false);
    }
  }, [session]);

  return { updateSegment, isUpdating, error };
};

// ============================================
// Delete Segment
// ============================================

interface UseDeleteSegmentReturn {
  deleteSegment: (id: number) => Promise<void>;
  isDeleting: boolean;
  error: string | null;
}

export const useDeleteSegment = (): UseDeleteSegmentReturn => {
  const { session } = useSession();
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const deleteSegment = useCallback(async (id: number): Promise<void> => {
    if (!session) throw new Error('Geen actieve sessie');
    try {
      setIsDeleting(true);
      setError(null);
      const response = await fetchWithAuth(`${API_URL}/segments/${id}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
      }, session);

      if (!response.ok) {
        throw new Error(`Failed to delete segment: ${response.statusText}`);
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Kon segment niet verwijderen';
      console.error('Error deleting segment:', err);
      setError(message);
      throw err;
    } finally {
      setIsDeleting(false);
    }
  }, [session]);

  return { deleteSegment, isDeleting, error };
};

// ============================================
// Evaluate Segment
// ============================================

interface UseEvaluateSegmentReturn {
  result: SegmentEvaluationResult | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export const useEvaluateSegment = (id: string | undefined): UseEvaluateSegmentReturn => {
  const { session } = useSession();
  const [result, setResult] = useState<SegmentEvaluationResult | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const evaluate = useCallback(async () => {
    if (!session || !id || id === 'new') return;
    try {
      setIsLoading(true);
      setError(null);
      const response = await fetchWithAuth(`${API_URL}/segments/${id}/evaluate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      }, session);

      if (!response.ok) {
        throw new Error(`Failed to evaluate segment: ${response.statusText}`);
      }

      const data = await response.json();
      setResult(data);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Kon segment niet evalueren';
      console.error('Error evaluating segment:', err);
      setError(message);
    } finally {
      setIsLoading(false);
    }
  }, [session, id]);

  useEffect(() => {
    evaluate();
  }, [evaluate]);

  return { result, isLoading, error, refetch: evaluate };
};

// ============================================
// Fetch Segment Labels
// ============================================

interface UseSegmentLabelsReturn {
  labels: string[];
  isLoading: boolean;
}

export const useSegmentLabels = (): UseSegmentLabelsReturn => {
  const { session } = useSession();
  const [labels, setLabels] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetchLabels = useCallback(async () => {
    if (!session) return;
    try {
      setIsLoading(true);
      const response = await fetchWithAuth(`${API_URL}/segments/labels`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      }, session);

      if (!response.ok) {
        throw new Error(`Failed to fetch segment labels: ${response.statusText}`);
      }

      const data = await response.json();
      setLabels(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error fetching segment labels:', err);
      setLabels([]);
    } finally {
      setIsLoading(false);
    }
  }, [session]);

  useEffect(() => {
    fetchLabels();
  }, [fetchLabels]);

  return { labels, isLoading };
};

// ============================================
// Fetch Clusters (for expression builder)
// ============================================

interface ClusterForPicker {
  id: number;
  name: string;
  type?: string;
  buildingCount?: number;
  buildings?: Array<{ id: string }>;
}

interface UseClustersReturn {
  clusters: ClusterForPicker[];
  isLoading: boolean;
}

export const useClusters = (): UseClustersReturn => {
  const { session } = useSession();
  const [clusters, setClusters] = useState<ClusterForPicker[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetchClusters = useCallback(async () => {
    if (!session) return;
    try {
      setIsLoading(true);
      const response = await fetchWithAuth(`${API_URL}/clusters`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      }, session);

      if (!response.ok) {
        throw new Error(`Failed to fetch clusters: ${response.statusText}`);
      }

      const data = await response.json();
      setClusters(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error fetching clusters:', err);
      setClusters([]);
    } finally {
      setIsLoading(false);
    }
  }, [session]);

  useEffect(() => {
    fetchClusters();
  }, [fetchClusters]);

  return { clusters, isLoading };
};
